import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { lastValueFrom } from "rxjs";
import {
  WhereUsedResult,
  WhereUsedRow,
  WhereUsedTreeNode,
} from "./where-used-tree.model";

@Injectable({
  providedIn: "root",
})
export class WhereUsedTreeService {
  constructor(private http: HttpClient) {}

  async getWhereUsed(
    customer: string,
    partNumber: string,
    maxDepth: number = 25
  ): Promise<WhereUsedResult> {
    const url = [
      "api",
      "whereUsed",
      customer,
      this.encodePartNumber(partNumber),
    ].join("/");

    return await lastValueFrom(
      this.http.get<WhereUsedResult>(url, {
        params: { maxDepth },
      })
    );
  }

  buildTree(rows: WhereUsedRow[]): WhereUsedTreeNode[] {
    const nodesByPath = new Map<string, WhereUsedTreeNode>();
    const roots: WhereUsedTreeNode[] = [];
    const sortedRows = rows
      .filter((row) => row.path != null && row.path.length > 1)
      .sort((a, b) => a.path.length - b.path.length);

    sortedRows.forEach((row) => {
      const key = this.getPathKey(row.path);

      if (nodesByPath.has(key)) {
        return;
      }

      const node: WhereUsedTreeNode = {
        ...row,
        isOpen: false,
        children: [],
      };
      nodesByPath.set(key, node);

      if (row.path.length === 2) {
        roots.push(node);
        return;
      }

      const parentKey = this.getPathKey(row.path.slice(0, row.path.length - 1));
      const parent = nodesByPath.get(parentKey);

      if (parent != null) {
        parent.children.push(node);
      }
    });

    return roots;
  }

  flattenOpenNodes(nodes: WhereUsedTreeNode[]): WhereUsedTreeNode[] {
    const result: WhereUsedTreeNode[] = [];

    nodes.forEach((node) => {
      result.push(node);

      if (node.isOpen && node.children.length > 0) {
        result.push(...this.flattenOpenNodes(node.children));
      }
    });

    return result;
  }

  private getPathKey(path: string[]) {
    return path.join(">");
  }

  private encodePartNumber(partNumber: string) {
    return encodeURIComponent(partNumber)
      .replace(/\(/g, "%28")
      .replace(/\)/g, "%29")
      .replace(/\!/g, "%21")
      .replace(/\*/g, "%2A")
      .replace(/\'/g, "%27")
      .replace(/\~/g, "%7E");
  }
}
